import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./Sidebar.css";
import { FiMessageSquare } from "react-icons/fi";
import {
  FiHome,
  FiUsers,
  FiBriefcase,
  FiCheckSquare,
  FiPieChart,
  FiSettings,
  FiDollarSign,
  FiInbox,
  FiLogOut,
  FiX,
  FiFileText,
} from "react-icons/fi";
import { resolveImageUrl } from "../../utils/resolveImageUrl";
import {
  ADMIN_ROLES,
  ROLE_VALUES,
  formatRoleLabel,
  hasAnyRole,
} from "../../utils/roles";
import { useState, useEffect } from "react";

const menu = [
  { name: "Home",          icon: FiHome,          path: "/",              roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Clients",       icon: FiUsers,         path: "/clients",       roles: ADMIN_ROLES },
  { name: "Projects",      icon: FiBriefcase,     path: "/projects",      roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Tasks",         icon: FiCheckSquare,   path: "/tasks",         roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Messages",      icon: FiMessageSquare, path: "/messages",      roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Team Overview", icon: FiPieChart,      path: "/overview",      roles: ADMIN_ROLES },
  { name: "Careers",       icon: FiBriefcase,     path: "/careers-admin", roles: ADMIN_ROLES },
  { name: "Applications",  icon: FiInbox,         path: "/careers-applications", roles: ADMIN_ROLES },
  { name: "Payroll",       icon: FiDollarSign,    path: "/finance",       roles: [ROLE_VALUES.FOUNDER] },
  { name: "Expenses",      icon: FiDollarSign,    path: "/finance/expenses",      roles: [ROLE_VALUES.FOUNDER] },
  { name: "Monthly Report",icon: FiFileText,      path: "/finance/monthly-report", roles: [ROLE_VALUES.FOUNDER] },
  { name: "Contact",       icon: FiInbox,         path: "/Contact",       roles: ADMIN_ROLES },
  { name: "Reports",       icon: FiPieChart,      path: "/reports",       roles: ADMIN_ROLES },
  { name: "Daily Report",  icon: FiFileText,      path: "/daily-report",  roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Brief",         icon: FiFileText,      path: "/brief",         roles: ADMIN_ROLES },
  { name: "Create User",   icon: FiUsers,         path: "/create-user",   roles: ADMIN_ROLES },
  { name: "Settings",      icon: FiSettings,      path: "/settings",      roles: [...ADMIN_ROLES, ROLE_VALUES.EMPLOYEE] },
  { name: "Users",         icon: FiUsers,         path: "/Users",         roles: ADMIN_ROLES },
];

const FINANCE_SIBLING_ROUTES = ["/finance/expenses", "/finance/monthly-report"];

function normalizePath(pathname) {
  const path = pathname.split("?")[0].split("#")[0];
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
}

function isMenuItemActive(path, pathname) {
  const current = normalizePath(pathname);

  if (path === "/finance") {
    if (FINANCE_SIBLING_ROUTES.some((route) => current === route || current.startsWith(`${route}/`))) {
      return false;
    }
    return (
      current === "/finance" ||
      current.startsWith("/finance/payroll") ||
      current === "/finance/deleted" ||
      current.startsWith("/finance/deleted/")
    );
  }

  if (path === "/") return current === "/";

  return current === path || current.startsWith(`${path}/`);
}

export default function Sidebar({ isOpen, onClose }) {
  const { currentUser, logout, totalUnreadCount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);

  const [avatarSrc, setAvatarSrc] = useState(null);

  useEffect(() => {
    const update = () => {
      try {
        const stored = localStorage.getItem("user");
        if (!stored) return;
        const user = JSON.parse(stored);
        const url = user?.avatar || user?.profilePicture?.url || user?.photo;
        setAvatarSrc(url ? resolveImageUrl(url) : null);
      } catch {
        // ignore
      }
    };

    update();
    window.addEventListener("userChanged", update);
    return () => window.removeEventListener("userChanged", update);
  }, []);

  useEffect(() => {
    const url = currentUser?.avatar || currentUser?.profilePicture?.url || currentUser?.photo;
    if (!url) { setAvatarSrc(null); return; }
    const hasTimestamp = url.includes("?t=");
    const final = hasTimestamp ? resolveImageUrl(url) : `${resolveImageUrl(url)}?t=${Date.now()}`;
    setAvatarSrc(final);
  }, [currentUser?.avatar, currentUser?.profilePicture?.url, currentUser?.photo]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!currentUser) return null;

  const userName =
    currentUser?.userName ||
    currentUser?.user?.userName ||
    currentUser?.email?.split("@")[0] ||
    "User";

  return (
    <aside className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebar-header">
        <span className="logo">Growth Station</span>
        <button className="sidebar-close-btn" onClick={onClose}>
          <FiX size={22} />
        </button>
      </div>

      <nav className="sidebar-menu">
        {menu
          .filter((item) => hasAnyRole(currentUser, item.roles))
          .map((item) => {
            const Icon = item.icon;
            const isMessages = item.name === "Messages";
            const active = isMenuItemActive(item.path, currentPath);
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`menu-item${active ? " active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={onClose}
              >
                <Icon size={20} />
                <span>{item.name}</span>
                {isMessages && totalUnreadCount > 0 && (
                  <span className="unread-badge">{totalUnreadCount}</span>
                )}
              </Link>
            );
          })}
      </nav>

      <div className="sidebar-auth">
        <div className="user-box">
          <div className="user-avatar">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="avatar"
                className="sidebar-avatar-img"
                key={avatarSrc}
              />
            ) : (
              <div className="avatar-placeholder">
                {userName?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </div>

          <div className="user-data">
            <span className="user-name">{userName}</span>
            <span className="user-role">{formatRoleLabel(currentUser?.role)}</span>
          </div>
        </div>

        <button className="logout-btn2" onClick={handleLogout}>
          <FiLogOut size={20} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}